import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/student-dashboard.css';
import PopupBox from './PopupBox';
import { createOutstationGatepass, getStudentStatus, getAllFaculties } from '../api/api';

// Professional SVG Icons
const Icons = {
  arrowLeft: <svg className="sa-icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>,
  plane: <svg className="sa-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"></path></svg>,
};

const OutstationGatepass = () => {
  const navigate = useNavigate();

  const getIndiaNow = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));

  const formatDateInput = (date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Minimum exit date is day after tomorrow (today + 2 days)
  const getMinExitDate = () => {
    const now = getIndiaNow();
    now.setDate(now.getDate() + 2);
    return formatDateInput(now);
  };
  const minExitDate = getMinExitDate();

  const [form, setForm] = useState({
    studentName: '',
    rollnumber: '',
    roomNumber: '',
    course: '',
    department: '',
    branch: '',
    contact: '',
    cpi: '',
    leaveDays: '',
    dateOut: '',
    timeOut: '',
    dateIn: '',
    timeIn: '',
    address: '',
    natureOfLeave: '',
    reasonOfLeave: '',
    classesMissed: '',
    missedDays: '',
    consent: false,
    attendanceUndertakingAccepted: false,
  });

  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState({ open: false, message: '' });
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [, setProfileLoading] = useState(true);
  const [proofFile, setProofFile] = useState(null);

  // PhD-specific: Instructor selection
  const [faculties, setFaculties] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState('');
  const [instructorSearch, setInstructorSearch] = useState('');
  const [showInstructorDropdown, setShowInstructorDropdown] = useState(false);
  const instructorDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (instructorDropdownRef.current && !instructorDropdownRef.current.contains(event.target)) {
        setShowInstructorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter faculties based on search
  const filteredFaculties = faculties.filter((faculty) => {
    const searchLower = instructorSearch.toLowerCase();
    return (
      faculty.name.toLowerCase().includes(searchLower) ||
      faculty.department.toLowerCase().includes(searchLower)
    );
  });

  // Get selected faculty details for display
  const selectedFacultyDetails = faculties.find((f) => f._id === selectedInstructor);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await getStudentStatus();
        const { studentName, rollnumber, department, branch, course, roomNumber, contactNumber } = res.data;
        setForm((prev) => ({
          ...prev,
          studentName: studentName || '',
          rollnumber: rollnumber || '',
          roomNumber: roomNumber || '',
          course: course || '',
          department: department || '',
          branch: branch || '',
          contact: contactNumber || '',
        }));

        // Fetch faculties for PhD students
        if (course === 'PhD') {
          try {
            const facultyRes = await getAllFaculties();
            setFaculties(facultyRes.data.faculties || []);
          } catch (err) {
            console.error('Failed to fetch faculties:', err);
          }
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      } finally {
        setProfileLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      if (name === 'dateOut') {
        if (next.dateIn && value && next.dateIn < value) {
          next.dateIn = '';
          next.timeIn = '';
        }
      }

      if (name === 'dateIn' && next.dateOut && value && value < next.dateOut) {
        next.dateIn = next.dateOut;
      }

      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;

    const {
      contact,
      leaveDays,
      dateOut, timeOut, dateIn, timeIn, consent,
    } = form;

    const requiredFields = [
      { key: 'studentName', label: 'Student Name' },
      { key: 'rollnumber', label: 'Roll Number' },
      { key: 'roomNumber', label: 'Room Number' },
      { key: 'course', label: 'Course' },
      { key: 'department', label: 'Department' },
      { key: 'contact', label: 'Contact Number' },
      // CPI is not required for PhD students
      ...(form.course !== 'PhD' ? [{ key: 'cpi', label: 'CPI' }] : []),
      { key: 'leaveDays', label: 'Leave Days' },
      { key: 'dateOut', label: 'Exit Date' },
      { key: 'timeOut', label: 'Exit Time' },
      { key: 'dateIn', label: 'Return Date' },
      { key: 'timeIn', label: 'Return Time' },
      { key: 'address', label: 'Address' },
      { key: 'natureOfLeave', label: 'Nature of Leave' },
      { key: 'reasonOfLeave', label: 'Reason for Leave' },
      { key: 'classesMissed', label: 'Number of classes to be missed' },
    ];

    const missingField = requiredFields.find(field => !form[field.key]);
    console.log('Form state:', form);
    console.log('Missing field:', missingField);
    if (missingField) {
      setPopup({ open: true, message: `Missing: ${missingField.label}` });
      return;
    }

    if (!/^\d{10}$/.test(contact)) {
      setPopup({ open: true, message: 'Contact number must be 10 digits.' });
      return;
    }

    if (!/^\d+$/.test(String(leaveDays)) || Number(leaveDays) < 1) {
      setPopup({ open: true, message: 'Leave days must be a valid number greater than 0.' });
      return;
    }

    // CPI validation only for non-PhD students
    if (form.course !== 'PhD') {
      if (!/^(\d|10)(\.\d{1,2})?$/.test(String(form.cpi)) || Number(form.cpi) < 0 || Number(form.cpi) > 10) {
        setPopup({ open: true, message: 'CPI must be between 0 and 10 (up to 2 decimal places).' });
        return;
      }
    }

    if (!['yes', 'no'].includes(form.classesMissed)) {
      setPopup({ open: true, message: 'Please select whether classes will be missed.' });
      return;
    }

    if (form.classesMissed === 'yes') {
      if (!/^\d+$/.test(String(form.missedDays)) || Number(form.missedDays) < 1) {
        setPopup({ open: true, message: 'Please enter number of classes to be missed (minimum 1).' });
        return;
      }
    }

    const reasonNeedsProof = /(emergency|medical|marriage)/i.test(form.reasonOfLeave || '');
    if (reasonNeedsProof && !proofFile) {
      setPopup({ open: true, message: 'Supporting document is mandatory for emergency, medical, or marriage reasons.' });
      return;
    }

    if (!consent) {
      setPopup({ open: true, message: 'You must confirm that the information is correct.' });
      return;
    }

    // PhD students must select an instructor
    if (form.course === 'PhD' && !selectedInstructor) {
      setPopup({ open: true, message: 'Please select your instructor.' });
      return;
    }

    const outDateTime = new Date(`${dateOut}T${timeOut}`);
    const inDateTime = new Date(`${dateIn}T${timeIn}`);
    const now = getIndiaNow();

    if (outDateTime <= now) {
      setPopup({ open: true, message: 'Out time must be in the future.' });
      return;
    }

    if (inDateTime <= outDateTime) {
      setPopup({ open: true, message: 'In time must be after out time.' });
      return;
    }

    setLoading(true);

    // Build FormData for file upload
    const formData = new FormData();
    Object.keys(form).forEach(key => {
      formData.append(key, form[key]);
    });
    if (proofFile) {
      formData.append('proofFile', proofFile);
    }
    // Add instructor for PhD students
    if (form.course === 'PhD' && selectedInstructor) {
      formData.append('instructorId', selectedInstructor);
    }

    createOutstationGatepass(formData)
      .then(() => {
        setSubmitSuccess(true);
        setPopup({ open: true, message: 'Outstation gatepass applied successfully!' });
      })
      .catch((err) => {
        const message = err?.response?.data?.message || 'Failed to submit gatepass.';
        setPopup({ open: true, message });
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="sd-shell">
      {/* Header */}
      <header className="sd-header">
        <div className="sd-header-brand">
          <span className="sd-logo">GoThru</span>
        </div>
        <button className="sa-back-btn" onClick={() => navigate('/student/gatepass')}>
          {Icons.arrowLeft} Back
        </button>
      </header>

      <main className="sd-main lg-main">
        <h1 className="lg-title">Outstation Gatepass</h1>

        <form className="lg-form" onSubmit={handleSubmit}>
          {/* Student Info Section */}
          <div className="lg-section">
            <div className="lg-section-label">STUDENT INFORMATION</div>
            <div className="lg-info-grid">
              <div className="lg-info-item">
                <span className="lg-info-label">Name</span>
                <span className="lg-info-value">{form.studentName || '—'}</span>
              </div>
              <div className="lg-info-item">
                <span className="lg-info-label">Roll No.</span>
                <span className="lg-info-value">{form.rollnumber || '—'}</span>
              </div>
              <div className="lg-info-item">
                <span className="lg-info-label">Department</span>
                <span className="lg-info-value">{form.department || '—'}</span>
              </div>
              <div className="lg-info-item">
                <span className="lg-info-label">Room No.</span>
                <span className="lg-info-value">{form.roomNumber || '—'}</span>
              </div>
              <div className="lg-info-item">
                <span className="lg-info-label">Contact</span>
                <span className="lg-info-value">{form.contact || '—'}</span>
              </div>
              {form.leaveDays > 0 && (
                <div className="lg-info-item">
                  <span className="lg-info-label">Leave Days</span>
                  <span className="lg-info-value">{form.leaveDays}</span>
                </div>
              )}
            </div>
          </div>

          {/* Course (from profile) */}
          <div className="lg-section">
            <div className="lg-section-label">COURSE</div>
            <div className="lg-info-item full">
              <span className="lg-info-label">Course</span>
              <span className="lg-info-value">{form.course || '—'}</span>
            </div>
          </div>

          {/* PhD Instructor Selection */}
          {form.course === 'PhD' && (
            <div className="lg-section">
              <div className="lg-section-label">INSTRUCTOR (GUIDE)</div>
              <div className="lg-field" ref={instructorDropdownRef} style={{ position: 'relative' }}>
                <label className="lg-label">Select Your Instructor <span style={{ color: '#e74c3c' }}>*</span></label>
                
                {/* Search Input */}
                <input
                  type="text"
                  className="lg-input"
                  placeholder="Type to search instructor..."
                  value={instructorSearch}
                  onChange={(e) => {
                    setInstructorSearch(e.target.value);
                    setShowInstructorDropdown(true);
                  }}
                  onFocus={() => setShowInstructorDropdown(true)}
                  autoComplete="off"
                />
                
                {/* Selected Instructor Display */}
                {selectedFacultyDetails && (
                  <div style={{
                    marginTop: '8px',
                    padding: '10px 14px',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '8px',
                    border: '1px solid #81c784',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <strong style={{ color: '#2e7d32' }}>{selectedFacultyDetails.name}</strong>
                      <div style={{ fontSize: '12px', color: '#558b2f' }}>{selectedFacultyDetails.department}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedInstructor('');
                        setInstructorSearch('');
                      }}
                      style={{
                        background: '#ef5350',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Clear
                    </button>
                  </div>
                )}

                {/* Dropdown List */}
                {showInstructorDropdown && !selectedInstructor && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '250px',
                    overflowY: 'auto',
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    marginTop: '4px'
                  }}>
                    {filteredFaculties.length === 0 ? (
                      <div style={{ padding: '12px 16px', color: '#888', textAlign: 'center' }}>
                        No instructors found
                      </div>
                    ) : (
                      filteredFaculties.map((faculty) => (
                        <div
                          key={faculty._id}
                          onClick={() => {
                            setSelectedInstructor(faculty._id);
                            setInstructorSearch('');
                            setShowInstructorDropdown(false);
                          }}
                          style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          <div style={{ fontWeight: '500', color: '#333' }}>{faculty.name}</div>
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{faculty.department}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="lg-section">
            <div className="lg-section-label">TRAVEL DETAILS</div>

            <div className="lg-row">
              <div className="lg-field">
                <label className="lg-label">Exit Date</label>
                <input
                  className="lg-input"
                  type="date"
                  name="dateOut"
                  min={minExitDate}
                  value={form.dateOut}
                  onChange={handleChange}
                />
              </div>
              <div className="lg-field">
                <label className="lg-label">Exit Time</label>
                <input
                  className="lg-input"
                  type="time"
                  name="timeOut"
                  value={form.timeOut}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="lg-row">
              <div className="lg-field">
                <label className="lg-label">Return Date</label>
                <input
                  className="lg-input"
                  type="date"
                  name="dateIn"
                  min={form.dateOut || minExitDate}
                  value={form.dateIn}
                  onChange={handleChange}
                />
              </div>
              <div className="lg-field">
                <label className="lg-label">Return Time</label>
                <input className="lg-input" type="time" name="timeIn" value={form.timeIn} onChange={handleChange} />
              </div>
            </div>

            <div className="lg-field">
              <label className="lg-label">Leave Days</label>
              <input
                className="lg-input"
                type="number"
                name="leaveDays"
                min="1"
                step="1"
                placeholder="Enter total leave days"
                value={form.leaveDays}
                onChange={handleChange}
              />
            </div>

            {/* CPI field is hidden for PhD students */}
            {form.course !== 'PhD' && (
              <div className="lg-field">
                <label className="lg-label">CPI <span style={{ color: '#e74c3c' }}>*</span></label>
                <input
                  className="lg-input"
                  type="number"
                  name="cpi"
                  min="0"
                  max="10"
                  step="0.01"
                  placeholder="Enter your CPI (e.g., 8.25)"
                  value={form.cpi}
                  onChange={handleChange}
                />
                <p className="lg-cpi-note">Students without a CPI must enter 0.</p>
              </div>
            )}

            <div className="lg-field">
              <label className="lg-label">Address During Leave</label>
              <input className="lg-input" type="text" name="address" placeholder="Full address where you'll stay" value={form.address} onChange={handleChange} />
            </div>
          </div>

          {/* Leave Details Section */}
          <div className="lg-section">
            <div className="lg-section-label">LEAVE DETAILS</div>

            <div className="lg-field">
              <label className="lg-label">Nature of Leave</label>
              <input className="lg-input" type="text" name="natureOfLeave" placeholder="e.g., Personal, Medical, Family" value={form.natureOfLeave} onChange={handleChange} />
            </div>

            <div className="lg-field">
              <label className="lg-label">Reason for Leave</label>
              <input className="lg-input" type="text" name="reasonOfLeave" placeholder="Detailed reason" value={form.reasonOfLeave} onChange={handleChange} maxLength={350} />
            </div>

            <div className="lg-field">
              <label className="lg-label">Number of Classes to be Missed <span style={{ color: '#e74c3c' }}>*</span></label>
              <div className="os-classes-row" style={{ marginTop: '6px' }}>
                <label>
                  <input
                    type="radio"
                    name="classesMissed"
                    value="no"
                    checked={form.classesMissed === 'no'}
                    onChange={handleChange}
                  />
                  No classes missed
                </label>
                <label>
                  <input
                    type="radio"
                    name="classesMissed"
                    value="yes"
                    checked={form.classesMissed === 'yes'}
                    onChange={handleChange}
                  />
                  Classes will be missed
                </label>
              </div>
              {form.classesMissed === 'yes' && (
                <input
                  className="lg-input"
                  style={{ marginTop: '10px' }}
                  type="number"
                  name="missedDays"
                  min="1"
                  step="1"
                  placeholder="Enter number of classes to be missed"
                  value={form.missedDays}
                  onChange={handleChange}
                />
              )}
            </div>

            <div className="lg-field" style={{ marginBottom: 0 }}>
              <label className="lg-label">Upload Supporting File (Optional)</label>
              <input
                className="lg-input"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file && file.size > 2 * 1024 * 1024) {
                    setPopup({ open: true, message: 'File size must be less than 2MB' });
                    e.target.value = '';
                    return;
                  }
                  setProofFile(file);
                }}
              />
              <span style={{ fontSize: '12px', color: '#888', marginTop: '4px', display: 'block' }}>
                PDF or Image (JPG, PNG) • Max 2MB
              </span>
              <span style={{ fontSize: '12px', color: '#c0392b', marginTop: '4px', display: 'block', fontWeight: 600 }}>
                * Please note that uploading supporting documents is mandatory for reasons such as emergency, medical, or marriage.
              </span>
            </div>
          </div>

          {/* Consent */}
          <label className="lg-consent">
            <input type="checkbox" name="consent" checked={form.consent} onChange={handleChange} />
            <span>I confirm that all information is correct. I understand that incorrect details may lead to denial of access.</span>
          </label>

          <label className="lg-consent" style={{ marginTop: '8px' }}>
            <input
              type="checkbox"
              name="attendanceUndertakingAccepted"
              checked={form.attendanceUndertakingAccepted}
              onChange={handleChange}
            />
            <span>I'll be deemed responsible if my attendance falls below 75%.</span>
          </label>

          <button type="submit" className="lg-submit-btn" disabled={loading}>
            {loading ? 'Submitting...' : <>{Icons.plane} Submit Application</>}
          </button>
        </form>
      </main>

      {/* Footer */}
      <div className="sd-footer">
        GoThru v1.1 • RGIPT Campus Access System
      </div>

      <PopupBox
        isOpen={popup.open}
        message={popup.message}
        onClose={() => {
          setPopup({ open: false, message: '' });
          if (submitSuccess) {
            navigate('/student');
          }
        }}
      />
    </div>
  );
};

export default OutstationGatepass;
